import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

interface Props {
  id: string;
  tag: string | undefined;
  tankGroup: string;
}

/**
 * Custom hook to calculate and update Onyx tank M3 calculations.
 * Bidirectional conversion between CM and M3 using formula: cm * 0.84 = m3
 *
 * @param props - An object containing the following properties:
 *   - id: A string representing the unique identifier for the input.
 *   - tag: tag that sits in input obj (onyxTankValue for CM, onyxTankM3 for M3).
 *   - tankGroup: identifier for the tank group (e.g., "tank-1", "tank-2").
 */
export const useOnyxTankConvert = ({ id, tag, tankGroup }: Props) => {
  const { setValue } = useFormContext();

  useEffect(() => {
    // Only run for onyx tank fields
    if (tag !== "onyxTankValue" && tag !== "onyxTankM3") return;

    const currentElement = document.querySelector(
      `[name="${id}"]`,
    ) as HTMLInputElement;
    if (!currentElement) return;

    // Add appropriate classes to current element
    if (tag === "onyxTankValue") {
      currentElement.classList.add("onyx-cm", tankGroup);
    } else if (tag === "onyxTankM3") {
      currentElement.classList.add("onyx-m3", tankGroup);
    }

    const handleConversion = () => {
      const value = currentElement.value;
      const fieldNumber = parseFloat(value);

      if (
        !isNaN(fieldNumber) &&
        fieldNumber !== null &&
        fieldNumber !== undefined &&
        value !== ""
      ) {
        if (tag === "onyxTankValue") {
          // Convert cm to m3
          const m3Element = document.querySelector(
            `.onyx-m3.${tankGroup}`,
          ) as HTMLInputElement;
          if (m3Element && m3Element.name) {
            const m3Value = +(fieldNumber * 0.84).toFixed(2);
            setValue(m3Element.name, m3Value);
          }
        } else if (tag === "onyxTankM3") {
          // Convert m3 to cm
          const cmElement = document.querySelector(
            `.onyx-cm.${tankGroup}`,
          ) as HTMLInputElement;
          if (cmElement && cmElement.name) {
            const cmValue = +(fieldNumber / 0.84).toFixed(2);
            setValue(cmElement.name, cmValue);
          }
        }
      } else {
        // Clear the opposite field when current field is empty
        if (tag === "onyxTankValue") {
          const m3Element = document.querySelector(
            `.onyx-m3.${tankGroup}`,
          ) as HTMLInputElement;
          if (m3Element && m3Element.name) {
            setValue(m3Element.name, "");
          }
        } else if (tag === "onyxTankM3") {
          const cmElement = document.querySelector(
            `.onyx-cm.${tankGroup}`,
          ) as HTMLInputElement;
          if (cmElement && cmElement.name) {
            setValue(cmElement.name, "");
          }
        }
      }
    };

    currentElement.addEventListener("change", handleConversion);

    return () => {
      currentElement.removeEventListener("change", handleConversion);
    };
  }, [id, tag, tankGroup, setValue]);
};
